import client from "libs/client"
import "../style/signupRequestsPage.css"
import { useEffect, useState } from "react";
import DatagridV2, { type SimpleColumn } from "ui-kit/src/Datagrid/DatagridV2";
import { _ } from "i18n";
import type { SignUpRequestRowResponse } from "libs/apiClient";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import { TableTextFilter } from "components/TableTextFilter/TableTextFilter";
import type { SelectValue } from "ui-kit/src/MultipleSelect/types";
import { setScrollBehavior } from "features/scrollPageBehaviorProvider/scrollPageBehaviorProvider";
import { DateTimeRangeCalendar } from "ui-kit/src/DateTimeRangeCalendar/DateTimeRangeCalendar";
import { formatDateTime } from "features/videoUploads/functions";
import { useI18n } from "features/translation/context";

export function SignupRequestsPage() {
    const [pagination, setPagination] = useState<{ pageIndex: number; pageSize: number }>({
        pageIndex: 0,
        pageSize: 15,
    });
    const { locale } = useI18n();
    const [filters, setFilters] = useState<{ [key: string]: string | number | undefined }>();
    const [filterValues, setFilterValues] = useState<{ [key: string]: string | undefined }>({});
    const [dateTimeRange, setDateTimeRange] = useState({ start: "", end: "" });
    const [sort, setSort] = useState<{ field: string, direction: string }>();
    const [selectedClub, setSelectedClub] = useState<SelectValue>()

    useEffect(() => { setScrollBehavior() }, [])

    const { data, isLoading } = client.getSignUpRequests.useQuery({
        page: pagination.pageIndex + 1, limit: pagination.pageSize, filters: [
            ...(filters?.clubId ? [{
                field: "clubId",
                type: "exact",
                value: filters.clubId,
            }] : []),
            ...(filters?.firstName ? [{
                field: "firstName",
                type: "search",
                value: filters.firstName,
            }] : []),
            ...(filters?.middleName ? [{
                field: "middleName",
                type: "search",
                value: filters.middleName,
            }] : []),
            ...(filters?.lastName ? [{
                field: "lastName",
                type: "search",
                value: filters.lastName,
            }] : []),
            ...(filters?.email ? [{
                field: "email",
                type: "search",
                value: filters.email,
            }] : []),
            ...(filters?.phoneNumber ? [{
                field: "phoneNumber",
                type: "search",
                value: filters.phoneNumber,
            }] : []),
            ...(filters?.playerId ? [{
                field: "playerId",
                type: "exact",
                value: filters.playerId,
            }] : []),
            ...(filters?.playerUserId ? [{
                field: "playerUserId",
                type: "exact",
                value: filters.playerUserId,
            }] : []),
            ...((dateTimeRange.start || dateTimeRange.end) ? [{
                field: "createdAt",
                type: "datetimerange",
                value: {
                    start: dateTimeRange.start === "" ? undefined : dateTimeRange.start,
                    end: dateTimeRange.end === "" ? undefined : dateTimeRange.end,
                },
            }] : []),
        ],
        order: sort
    })

    const { data: clubs, isLoading: isClubsLoading } = client.getOptionsClubsOfSignUpRequests.useQuery({})

    const handleSetFilter = (key: string, value?: number | string,) => {
        setFilters({ ...filters, [key]: value });
        setPagination({ ...pagination, pageIndex: 0 });
    }

    const handleClearFilter = (key: string) => {
        setFilters({ ...filters, [key]: undefined })
    }

    const onDateTimeChange = (start: string, end: string) => {
        setDateTimeRange({ start: start, end: end });
    };

    const toggleSortOrder = (name: string) => {
        let newOrder: "ASC" | "DESC" | null = "DESC"

        if (sort?.field === name) {
            if (sort.direction === "DESC") {
                newOrder = "ASC"
            }
            if (sort.direction === "ASC") {
                newOrder = null
            }
        }

        if (newOrder !== null) {
            setSort({ field: name, direction: newOrder })
        } else {
            setSort(undefined)
        }

    };

    const generateSortArrows = (columnName: string) => (
        <div className="sortArrows" onClick={() => toggleSortOrder(columnName)}>
            {sort?.field === columnName ? (
                sort.direction === "ASC" ? (
                    <span className="icon-new icon-16 icon-select-arrow-down up" />
                ) : (
                    <span className="icon-new icon-16 icon-select-arrow-down down" />
                )
            ) : (
                <>
                    <span className="icon-new icon-16 icon-select-arrow-down up" />
                    <span className="icon-new icon-16 icon-select-arrow-down down" />
                </>
            )}
        </div>
    );

    const generateColumnHeader = (columnName: string, label: string, isNumber?: boolean) => () => (
        <div className="tableHeader">
            <TableTextFilter
                value={filterValues[columnName] || ""}
                onChange={(value) => setFilterValues({ ...filterValues, [columnName]: value })}
                number={isNumber}
                clear={() => {
                    handleClearFilter(columnName);
                    setFilterValues({ ...filterValues, [columnName]: undefined })
                }}
                onSubmit={(value: string) => handleSetFilter(columnName, value)}
                placeholder={label}
            />
            {generateSortArrows(columnName)}
        </div>
    );

    const simpleColumns: SimpleColumn<SignUpRequestRowResponse>[] = [
        {
            id: "club",
            key: "clubId",
            header: () => (
                <div className="tableHeader">
                    <SelectComponent
                        value={selectedClub}
                        handleChange={(value) => {
                            setSelectedClub(value);
                            handleSetFilter("clubId", value?.value)
                        }}
                        placeholder="club"
                        items={clubs?.data}
                        isCleareable
                        isLoading={isClubsLoading}
                    />
                    {generateSortArrows("clubName")}
                </div>
            ),
            cell: (value, row) => row.clubName ?? "-",
        },
        {
            id: "firstName",
            key: "firstName",
            header: generateColumnHeader("firstName", "first name"),
            cell: (value, row) => row.firstName ?? "-",
        },
        {
            id: "middleName",
            key: "middleName",
            header: generateColumnHeader("middleName", "middle name"),
            cell: (value, row) => <div className="thinCell">{row.middleName ?? "-"}</div>,
        },
        {
            id: "lastName",
            key: "lastName",
            header: generateColumnHeader("lastName", "last name"),
            cell: (value, row) => <div className="thinCell">{row.lastName ?? "-"}</div>,
        },
        {
            id: "email",
            key: "email",
            header: generateColumnHeader("email", "email"),
            cell: (value, row) => <div className="thinCell">{row.email ?? "-"}</div>,
        },
        {
            id: "phoneNumber",
            key: "phoneNumber",
            header: generateColumnHeader("phoneNumber", "phone", true),
            cell: (value, row) => <div className="thinCell">{row.phoneNumber ?? "-"}</div>,
        },
        {
            id: "playerId",
            key: "playerId",
            header: generateColumnHeader("playerId", "player id"),
            cell: (value, row) => <div className="thinCell"><div>{row.playerId ? row.playerId : "-"}</div></div>,
        },
        {
            id: "playerUserId",
            key: "playerUserId",
            header: generateColumnHeader("playerUserId", "user id"),
            cell: (value, row) => <div className="thinCell"><div>{row.playerUserId ? row.playerUserId : "-"}</div></div>,
        },
        {
            id: "createdAt",
            key: "createdAt",
            header: () => (
                <div className="tableHeader">
                    <DateTimeRangeCalendar
                        placeholder="Created"
                        apiFormat
                        onChange={onDateTimeChange}
                        start={dateTimeRange.start}
                        end={dateTimeRange.end}
                        locale={locale}
                    />
                    {generateSortArrows("createdAt")}
                </div>
            ),
            cell: (value, row) => <div className="dateTimeCell">{formatDateTime(row.createdAt) ?? "-"}</div>,
        },
    ]

    return (
        <div className="signupRequestsPage-container">
            <div className="pageTitle"> {_("label.signup_requests")} </div>
            <DatagridV2
                getRowLink={(row) => `/player-users/user/${row.id}/#signupRequest`}
                isLoading={isLoading}
                simpleColumns={simpleColumns}
                data={data?.data.items ?? []}
                pageCount={data?.data?.pages ?? 1}
                pagination={pagination}
                setPagination={setPagination}
                noResults={_("datatables.zero_records")} />
        </div>
    )
}
